// src/lib/limulo/wallet.ts
import { unwrapItems } from '@/lib/limulo/model';
import type { Wallet } from '@/types/qbank';

/**
 * `GET /v1/me/wallet` della facciata Limulo → lo STESSO `Wallet[]` che la TMA legge da QBank
 * (`types/qbank.ts`), cosi' i cinque consumatori non sanno da dove arriva il saldo.
 *
 * Contratto: `qlash-limulo` `packages/app-qlash-api/docs/PUBLIC_API_CONTRACT.md`, «Wallet
 * dell'utente» (D-WALLET, 2026-09-30). Busta standard, `data.items[]` =
 * `{ currencyId, name, code, model, isDefault, isEnable, amount, level?, toNextLevel? }`, una pagina sola.
 *
 * Cio' che la facciata NON manda, e come lo si riempie (decisione L-2717 (a)):
 *   · `sub`          → `''`  (il soggetto lo ricava la facciata dalla sessione; non esce mai, e il client non lo manda)
 *   · `exchange`     → `null`
 *   · `defaultCoin`  → `null`
 * Gli altri campi passano 1:1. `level` / `toNextLevel` ci sono solo sulle monete Exp (`model` 1): assenti → `null`,
 * che e' anche il segno con cui i consumatori riconoscono una moneta Exp (`level != null`).
 *
 * ⚠️ `model` NON entra in `Wallet` (il tipo e' lo specchio di QBank, che non l'ha): una moneta League (`model` 2)
 * si legge come una moneta di conto, esattamente com'e' oggi dal QBank diretto.
 *
 * ⚠️ Difensivo sui TIPI, non sul contratto: un campo del tipo sbagliato diventa `null` (o `''`), mai un valore
 * inventato. Un item senza `currencyId` si scarta: e' l'identita' della moneta (`code` e `name` sono mutabili),
 * e senza non c'e' modo di dire quale moneta sia.
 */
const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const textOf = (v: unknown): string => (typeof v === 'string' ? v : '');
const boolOrNull = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const numberOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

export function walletOf(item: unknown): Wallet | null {
  if (!isRecord(item)) return null;
  if (typeof item.currencyId !== 'string' || item.currencyId === '') return null;
  return {
    currencyId: item.currencyId,
    sub: '',
    name: textOf(item.name),
    code: textOf(item.code),
    exchange: null,
    isDefault: boolOrNull(item.isDefault),
    amount: numberOrNull(item.amount),
    defaultCoin: null,
    isEnable: boolOrNull(item.isEnable),
    level: numberOrNull(item.level),
    toNextLevel: numberOrNull(item.toNextLevel),
  };
}

/** La busta intera → `Wallet[]`. Una busta senza `items` da' `[]`. */
export function walletsOf(envelope: unknown): Wallet[] {
  return unwrapItems<unknown>(envelope).flatMap((item) => {
    const w = walletOf(item);
    return w ? [w] : [];
  });
}
